import { DialogTitle } from '@headlessui/react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import BaseDialog from './BaseDialog';
import { useState, useEffect } from 'react';

export default function ConfirmDialog({
                                        open,
                                        title = "Confirm",
                                        description = "Are you sure?",
                                        warnings = [],
                                        confirmText = "Confirm",
                                        cancelText = "Cancel",
                                        onConfirm,
                                        onCancel,
                                        icon,
                                        iconBg = "bg-red-100",
                                        iconColor = "text-red-600",
                                        requireTextConfirmation = false,
                                        confirmationText = "",
                                        confirmationPlaceholder = "Type to confirm",
                                        requirePasswordConfirmation = false,
                                        passwordLabel = "Enter your current password to confirm",
                                        passwordPlaceholder = "Current password",
                                        isLoading = false,
                                        requireAcknowledge = false,
                                        acknowledgeLabel = "I understand - proceed anyway",
                                      }) {
  const IconComponent = icon || ExclamationTriangleIcon;
  const [inputValue, setInputValue] = useState('');
  const [passwordValue, setPasswordValue] = useState('');
  const requiresInput = requireTextConfirmation || requirePasswordConfirmation;
  const [isConfirmEnabled, setIsConfirmEnabled] = useState(!requiresInput);
  const [acknowledged, setAcknowledged] = useState(false);

  // Reset input when dialog opens/closes
  useEffect(() => {
    if (open) {
      setInputValue('');
      setPasswordValue('');
      setIsConfirmEnabled(!requiresInput);
      setAcknowledged(false);
    }
  }, [open, requiresInput]);

  // Check if input matches required confirmation text
  useEffect(() => {
    if (requireTextConfirmation) {
      setIsConfirmEnabled(inputValue === confirmationText);
    } else if (requirePasswordConfirmation) {
      setIsConfirmEnabled(passwordValue.length > 0);
    }
  }, [inputValue, confirmationText, requireTextConfirmation, requirePasswordConfirmation, passwordValue]);

  const confirmAllowed = isConfirmEnabled && (!requireAcknowledge || acknowledged);

  const handleConfirm = () => {
    if (confirmAllowed && !isLoading) {
      if (requirePasswordConfirmation) {
        onConfirm(passwordValue);
      } else {
        onConfirm();
      }
    }
  };

  return (
    <BaseDialog
      open={open}
      onClose={onCancel}
      maxWidthClass="sm:max-w-lg"
      panelClassName="px-4 pb-4 pt-5 sm:p-6"
    >
            <div className="sm:flex sm:items-start">
              <div className={`mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${iconBg} sm:mx-0 sm:h-10 dark:bg-gray-700/40`}>
                <IconComponent className={`h-6 w-6 ${iconColor}`} />
              </div>
              <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left space-y-3">
                <DialogTitle as="h3" className="text-base font-semibold text-gray-900 dark:text-gray-100">
                  {title}
                </DialogTitle>
                <div className="text-sm text-gray-500 dark:text-gray-300 space-y-2">
                  <p>{description}</p>

                  {warnings.length > 0 && (
                    <div className="space-y-3 mt-4">
                      {warnings.map((warn, idx) => (
                        <div key={idx} className="flex items-center space-x-3">
                          <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500 flex-shrink-0 dark:text-yellow-400" />
                          <p className="text-sm text-gray-700 dark:text-gray-200 leading-snug">
                            <span className="font-semibold">{warn.label}:</span> {warn.description}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}

                  {requireTextConfirmation && (
                    <div className="mt-4 space-y-2">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        Please type <span className="font-mono font-semibold text-red-600 dark:text-red-400">{confirmationText}</span> to confirm:
                      </p>
                      <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        placeholder={confirmationPlaceholder}
                        className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-500"
                        autoFocus
                      />
                    </div>
                  )}

                  {requirePasswordConfirmation && (
                    <div className="mt-4 space-y-2">
                      <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                        {passwordLabel}
                      </p>
                      <input
                        type="password"
                        value={passwordValue}
                        onChange={(e) => setPasswordValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' && passwordValue && !isLoading) handleConfirm(); }}
                        placeholder={passwordPlaceholder}
                        className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 dark:placeholder-gray-500"
                        autoFocus
                      />
                    </div>
                  )}

                  {requireAcknowledge && (
                    <label className="mt-3 flex items-start gap-2 text-sm text-red-700 dark:text-red-300">
                      <input
                        type="checkbox"
                        checked={acknowledged}
                        onChange={(e) => setAcknowledged(e.target.checked)}
                        className="mt-0.5 rounded border-gray-300"
                      />
                      {acknowledgeLabel}
                    </label>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={handleConfirm}
                disabled={!confirmAllowed || isLoading}
                className={`inline-flex w-full justify-center rounded-md px-3 py-2 text-sm font-semibold text-white shadow-sm sm:ml-3 sm:w-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 ${
                  confirmAllowed && !isLoading
                    ? 'bg-red-600 hover:bg-red-500 cursor-pointer'
                    : 'bg-red-400 cursor-not-allowed opacity-60'
                }`}
              >
                {isLoading ? 'Saving...' : confirmText}
              </button>
              <button
                type="button"
                onClick={onCancel}
                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto dark:bg-gray-800 dark:text-gray-100 dark:ring-gray-600 dark:hover:bg-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              >
                {cancelText}
              </button>
            </div>
    </BaseDialog>
  );
}